import {Alert} from "react-bootstrap";
import {ApiError} from "../api/http";
import {ApiErrorCode, FieldError, GenericErrorCodes} from "../api/errors";
import {errors, format} from "../i18n";

// A page's own wording for an error: by the API's code ('email_in_use'), or by status (404) for whatever the code
// doesn't make specific (a page knows what wasn't found).
export type ErrorMessages = Partial<Record<ApiErrorCode, string>> & { [status: number]: string };

// The status's generic code, for errors that don't say one (an older API, or one the site doesn't know yet).
const STATUS_CODES: { [status: number]: ApiErrorCode } = {
    400: 'bad_request', 401: 'unauthorized', 403: 'forbidden', 404: 'not_found', 405: 'method_not_allowed',
    409: 'conflict', 429: 'too_many_requests', 503: 'unavailable'
};

// What to tell the user, most specific first: the page's words for the code; the catalogue's for a specific code; the
// page's words for the status; the catalogue's generic words. 5xx errors end with the reference for support.
export function errorMessage(error: unknown, messages?: ErrorMessages): string {
    if (!(error instanceof ApiError)) {
        return error instanceof Error ? error.message : String(error);
    }
    if (error.status === 0) {
        return errors.unreachable;
    }
    const code = error.code;
    const text = (code && messages?.[code])
        ?? (code && !GenericErrorCodes.includes(code) ? format(errors.errorMessages[code], error.params) : undefined)
        ?? messages?.[error.status]
        ?? errors.errorMessages[code ?? STATUS_CODES[error.status] ?? (error.status >= 500 ? 'server_error' : 'bad_request')];
    return error.status >= 500 && error.requestId ? `${text} ${format(errors.errorReference, { requestId: error.requestId })}` : text;
}

// What's wrong with one of the request's fields (or any item of a list field, for 'attachments'), for showing under it.
export function fieldError(error: unknown, field: string): string | undefined {
    const found = fieldErrorsOf(error).find(x => matchesField(x.field, field));
    return found ? format(errors.fieldErrorMessages[found.error], found.params) : undefined;
}

// "Price 2, Amount" for prices[1].amount.
export function fieldLabel(field: string): string {
    return field.split('.').map(part => {
        const match = /^(\w+)\[(\d+)]$/.exec(part);
        const label = errors.fieldLabels[match ? match[1] : part] ?? part;
        return match ? format(errors.listItemLabel, { label, number: Number(match[2]) + 1 }) : label;
    }).join(', ');
}

function matchesField(actual: string, field: string): boolean {
    return actual === field || actual.startsWith(`${field}[`) || actual.startsWith(`${field}.`);
}

function fieldErrorsOf(error: unknown): FieldError[] {
    return error instanceof ApiError ? error.fields : [];
}

// An error, in words. With fields the API refused, lists what's wrong with each one, but those in shownFields, which the
// form shows under the fields themselves (fieldError).
export function ErrorAlert({error, messages, shownFields, onClose}: {
    error: unknown, messages?: ErrorMessages, shownFields?: string[], onClose?: () => void
}) {
    if (!error) {
        return null;
    }
    const fields = fieldErrorsOf(error);
    const listed = fields.filter(x => !shownFields?.some(shown => matchesField(x.field, shown)));
    const text = fields.length && !listed.length ? errors.checkFieldsBelow : errorMessage(error, messages);
    return (
        <Alert variant="danger" dismissible={!!onClose} onClose={onClose}>
            {text}
            {listed.length > 0 && (
                <ul className="mb-0 mt-2">
                    {listed.map(x => <li key={x.field}>{fieldLabel(x.field)}: {format(errors.fieldErrorMessages[x.error], x.params)}</li>)}
                </ul>
            )}
        </Alert>
    );
}
